import React, { useCallback, useState, useEffect, useMemo } from 'react';
import Icon from '../Icon';
import AdditionalOrderInfo from './Checkout.AdditionalOrderInfo';
import Cart from './Checkout.Cart';
import ConfirmOrder from './Checkout.ConfirmOrder';
import DeliveryMethods from './Checkout.DeliveryMethods';
import PaymentMethods from './Checkout.PaymentMethods';
import PrivateCustomerInfo from './Checkout.PrivateCustomerInfo';
import BusinessCustomerInfo from './Checkout.BusinessCustomerInfo';
import EmptyCart from './Checkout.EmptyCart';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { StyledCheckout } from './styles/Checkout.styling';
import PaymentWidget from '../Payments/PaymentWidget';
import BlockArea from './Checkout.BlockArea';
import {
  PaymentIntegrationType,
  ShippingIntegrationType,
} from '../../constants';
import { CartState } from '../../Reducers/Cart.reducer';
import CheckoutSection from './utils/Checkout.Section';
import CheckoutSectionContent from './utils/Checkout.SectionContent';
import SuggestedProductsSliderCheckout from '../SuggestedProducts/Checkout/SuggestedProductSlider.Checkout';
import { translate } from '../../Services/translation';
import { useDispatch } from 'react-redux';
import { loadSuggestedProducts } from '../../Actions/SuggestedProducts.action';
import { reloadPayment } from '../../Actions/Checkout.action';

const Checkout = (cart: CartState) => {
  const dispatch = useDispatch();
  const { payload } = useTypedSelector((state) => state.checkout);
  const suggestedProducts = useTypedSelector((state) => state.suggestedProducts);
  const orderRows = cart.orderRows;
  const hasOrderRows = orderRows && Object.keys(orderRows).length > 0;
  const numProducts = hasOrderRows
    ? Object.values(orderRows).length
    : 0;
  const {
    message,
    isBusinessCustomer,
    paymentWidget,
    deliveryMethods,
    selectedDeliveryMethod,
    selectedPaymentMethod,
    paymentMethods,
    paymentErrorMessage,
  } = payload;

  const [headerBlockElements, setHeaderBlockElements] = useState<any[]>(
    []
  );
  const [footerBlockElements, setFooterBlockElements] = useState<any[]>(
    []
  );
  const [paymentWidgetVisibility, setPaymentWidgetVisibility] = useState(
    true
  );
  const [paymentOptionVisibility, setPaymentOptionVisibility] = useState(
    true
  );
  const [
    customerDetailsVisibility,
    setCustomerDetailsVisibility,
  ] = useState(true);
  const [deliveryWidgetVisibility, setDeliveryWidgetVisibility] = useState(
    true
  );
  const [confirmOrderVisibility, setConfirmOrderVisibility] = useState(
    true
  );
  const [signUpCheckboxVisibility, setSignUpCheckboxVisibility] = useState(
    true
  );
  const [shippingOptionVisibility, setShippingOptionVisibility] = useState(
    true
  );

  const checkIntegrationTypeExist = useCallback(
    (integrationTypeCheck) => {
      return deliveryMethods.find(
        (method) => method.integrationType === integrationTypeCheck
      );
    },
    [DeliveryMethods]
  );

  const resetVisibilityValues = useCallback(() => {
    setShippingOptionVisibility(true);
    setPaymentOptionVisibility(true);
    setCustomerDetailsVisibility(true);
    setDeliveryWidgetVisibility(true);
    setPaymentWidgetVisibility(true);
    setConfirmOrderVisibility(true);
    setSignUpCheckboxVisibility(true);
  }, []);

  // Show or hide different sections depending on payment and delivery methods.
  useEffect(() => {
    if (headerBlockElements?.length < 1) {
      const headerBlockElementsContainer = document.getElementById(
        'checkout-header-blocks'
      );
      const headerBlockElements = headerBlockElementsContainer
        ? Array.from(headerBlockElementsContainer.children)
        : [];
      setHeaderBlockElements(headerBlockElements);
      if (headerBlockElementsContainer)
        headerBlockElementsContainer.remove();
    }

    if (footerBlockElements?.length < 1) {
      const footerBlockElementsContainer = document.getElementById(
        'checkout-footer-blocks'
      );
      const footerBlockElements = footerBlockElementsContainer
        ? Array.from(footerBlockElementsContainer.children)
        : [];
      setFooterBlockElements(footerBlockElements);
      if (footerBlockElementsContainer)
        footerBlockElementsContainer.remove();
    }

    resetVisibilityValues();

    switch (selectedPaymentMethod?.integrationType) {
      case PaymentIntegrationType.IframeCheckout:
        setCustomerDetailsVisibility(false);
        setConfirmOrderVisibility(false);
        break;
      case PaymentIntegrationType.PaymentWidgets:
        setConfirmOrderVisibility(false);
        setSignUpCheckboxVisibility(false);
        break;
      case PaymentIntegrationType.DirectPayment:
        setPaymentWidgetVisibility(false);
        break;
    }

    if (
      checkIntegrationTypeExist(ShippingIntegrationType.DeliveryCheckout)
    ) {
      return setShippingOptionVisibility(false);
    }

    if (
      checkIntegrationTypeExist(ShippingIntegrationType.PaymentCheckout)
    ) {
      setDeliveryWidgetVisibility(false);
      return;
    }

    if (
      selectedDeliveryMethod?.integrationType ===
      ShippingIntegrationType.Inline
    ) {
      setDeliveryWidgetVisibility(false);
    }
  }, [
    selectedDeliveryMethod,
    selectedPaymentMethod,
    deliveryMethods,
    paymentMethods,
    resetVisibilityValues,
    checkIntegrationTypeExist,
  ]);

  const responseString = paymentWidget
    ? paymentWidget.responseString
    : null;
  const updateKey = paymentWidget ? paymentWidget._force_update : null;

  if (numProducts <= 0) {
    return (
      <StyledCheckout>
        <EmptyCart />
      </StyledCheckout>
    );
  }

  useState(() => {
    dispatch(loadSuggestedProducts(dispatch))
  })

  const suggestedProductSliderCheckout = useMemo(() => {
    return (
      suggestedProducts && suggestedProducts?.products?.length > 0 && (
        <SuggestedProductsSliderCheckout
          suggestedProducts={suggestedProducts.products}
          sliderIntervalInSec={suggestedProducts.sliderIntervalInSec}
        />
      ))
  }, [suggestedProducts])

  return (
    <StyledCheckout>
      {headerBlockElements && <BlockArea blocks={headerBlockElements} />}
      <div className="checkout-content !max-w-[900px]">
        {message && (
          <div className="checkout__message">
            <Icon className="checkout__message-icon" name="info" />
            <span>{message}</span>
          </div>
        )}

        <div className="checkout__wrapper">
          {paymentErrorMessage && (
            <div className="bg-[#D44336] text-white px-8 py-6 flex flex-row items-center gap-4">
              <Icon name="error-triangle" className='w-[66px] h-[66px]' />
              <span className='font-satoshi'>{paymentErrorMessage}</span>
            </div>
          )}
          <div className="checkout__sections-wrapper">
            <Cart />
          </div>
          <div className="checkout__sections-wrapper">
            <CheckoutSection
              className='!px-0'
              title={translate("suggestedproducts-title")}
              headerClassName='!px-[1.6rem] lg:!px-[2.4rem]'
            >
              {suggestedProductSliderCheckout}
            </CheckoutSection>
          </div>
          <div className="checkout__sections-wrapper">
            {!isBusinessCustomer && customerDetailsVisibility && (
              <PrivateCustomerInfo showSignUp={signUpCheckboxVisibility} />
            )}
            {isBusinessCustomer && customerDetailsVisibility && (
              <BusinessCustomerInfo />
            )}
            {shippingOptionVisibility && <DeliveryMethods />}
            {paymentOptionVisibility && <PaymentMethods />}
            {!paymentWidget && !paymentWidgetVisibility && (
              <AdditionalOrderInfo />
            )}
            {confirmOrderVisibility && <ConfirmOrder />}
          </div>
          {paymentWidget && paymentWidgetVisibility && (
            <div className="checkout__sections-wrapper">
              <PaymentWidget
                key={updateKey}
                responseString={responseString}
              />
            </div>
          )}
        </div>
      </div>
      {footerBlockElements && (
        <BlockArea blocks={footerBlockElements} isFooter={true} />
      )}
    </StyledCheckout>
  );
};
export default Checkout;